import { useEffect } from 'react';

const BRAND = 'VVVSI Trust | Vallalar Velai Vaaipu Sevai Iyakkam';

/**
 * useSEO - Set document.title, meta description and robots directive per page.
 *
 * @param {object} opts
 * @param {string} opts.title       page-specific title (without brand suffix)
 * @param {string} opts.description page-specific meta description
 * @param {boolean} opts.noindex    if true, injects <meta name="robots" content="noindex,nofollow,noarchive">
 *                                  and is automatically REMOVED on unmount so public pages remain indexable.
 */
export const useSEO = ({ title, description, noindex = false } = {}) => {
    useEffect(() => {
        // ---- Title ----
        document.title = title ? `${title} | ${BRAND}` : BRAND;

        // ---- Description ----
        if (description) {
            let meta = document.querySelector('meta[name="description"]');
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('name', 'description');
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', description);

            const og = document.querySelector('meta[property="og:description"]');
            if (og) og.setAttribute('content', description);
            const ogt = document.querySelector('meta[property="og:title"]');
            if (ogt && title) ogt.setAttribute('content', `${title} | ${BRAND}`);
        }

        // ---- Robots (noindex for admin surfaces) ----
        let robots = document.querySelector('meta[name="robots"]');
        if (noindex) {
            if (!robots) {
                robots = document.createElement('meta');
                robots.setAttribute('name', 'robots');
                robots.setAttribute('data-managed-by', 'useSEO');
                document.head.appendChild(robots);
            }
            robots.setAttribute('content', 'noindex,nofollow,noarchive,nosnippet');
            // Also add a googlebot-specific tag for good measure
            let googlebot = document.querySelector('meta[name="googlebot"]');
            if (!googlebot) {
                googlebot = document.createElement('meta');
                googlebot.setAttribute('name', 'googlebot');
                googlebot.setAttribute('data-managed-by', 'useSEO');
                document.head.appendChild(googlebot);
            }
            googlebot.setAttribute('content', 'noindex,nofollow,noarchive,nosnippet');
        } else if (robots?.getAttribute('data-managed-by') === 'useSEO') {
            // Only remove if we added it — respect any static robots tag in index.html.
            robots.remove();
            const googlebot = document.querySelector('meta[name="googlebot"]');
            if (googlebot?.getAttribute('data-managed-by') === 'useSEO') {
                googlebot.remove();
            }
        }

        return () => {
            // Clean up managed robots tag when the caller unmounts (e.g. leaving /admin)
            const r = document.querySelector('meta[name="robots"][data-managed-by="useSEO"]');
            if (r) r.remove();
            const g = document.querySelector('meta[name="googlebot"][data-managed-by="useSEO"]');
            if (g) g.remove();
        };
    }, [title, description, noindex]);
};

export default useSEO;
